/**
 * Shared rules for proceeding detail pages: which ECFS rows count as
 * comment/reply for the Filings tab and Comment Summaries, and how to bucket
 * them into comment vs reply periods (matches FilingsSection).
 */

export interface ProceedingFilingPeriodInput {
  id_submission: string;
  date_received?: string;
  date_submission?: string;
  submissiontype?: { short: string };
  filing_type?: string;
}

export interface NprmTimelineForPeriod {
  comments_period_start: string;
  comments_period_end: string | null;
  reply_comments_period_start: string | null;
  reply_comments_period_end: string | null;
}

export interface PeriodSubmissionForMap {
  period_type: 'comments' | 'reply_comments';
}

const COMMENT_PERIOD_TYPE_CODES = new Set([
  'COMMENT',
  'EXPRESS-COMMENT',
  'EXPRESS COMMENT',
]);

const REPLY_PERIOD_TYPE_CODES = new Set([
  'REPLY',
  'REPLY_COMMENT',
  'REPLY_COMM',
  'REPLY COMM',
  'REPLY TO COMMENTS',
]);

const COMMENT_OR_REPLY_SHORT_CODES = new Set([
  ...COMMENT_PERIOD_TYPE_CODES,
  ...REPLY_PERIOD_TYPE_CODES,
]);

export function isCommentOrReplyFiling(filing: ProceedingFilingPeriodInput): boolean {
  const raw = (filing.submissiontype?.short || filing.filing_type || '').trim().toUpperCase();
  return COMMENT_OR_REPLY_SHORT_CODES.has(raw);
}

/**
 * Returns the comment/reply bucket implied by the filer's chosen submission
 * type, ignoring filing date. `null` if the type is unknown (caller should
 * fall back to date-window bucketing).
 *
 * Trusting type over date is intentional: ECFS lets filers submit a COMMENT
 * before the Federal Register publication date (the formal start of the
 * comment window) and after the deadline. Those filings are still comments
 * in every meaningful sense — they belong in the Comment Period tab and in
 * the scope of a comment-period summary. Date-based bucketing alone hides
 * pre/post-window comments behind the "Other" bucket.
 */
function periodFromSubmissionType(
  filing: ProceedingFilingPeriodInput,
): 'comments' | 'reply_comments' | null {
  const raw = (filing.submissiontype?.short || filing.filing_type || '').trim().toUpperCase();
  if (COMMENT_PERIOD_TYPE_CODES.has(raw)) return 'comments';
  if (REPLY_PERIOD_TYPE_CODES.has(raw)) return 'reply_comments';
  return null;
}

/** Same express_comment handling as FilingsSection after retrieval. */
export function filterDefenseExpressComments<T extends { express_comment?: unknown }>(
  filings: T[]
): T[] {
  return filings.filter((f) => {
    const raw = f.express_comment;
    if (typeof raw === 'boolean') return !raw;
    if (typeof raw === 'number') return raw <= 0;
    if (typeof raw === 'string') return !(raw === '1' || raw.toLowerCase() === 'true');
    return true;
  });
}

export function getProceedingFilingPeriod(
  filing: ProceedingFilingPeriodInput,
  ctx: {
    periodSubmissionsMap: Map<string, PeriodSubmissionForMap>;
    activeTimeline: NprmTimelineForPeriod | null;
  }
): 'comments' | 'reply_comments' | 'other' {
  const { periodSubmissionsMap, activeTimeline } = ctx;

  const periodSubmission = periodSubmissionsMap.get(filing.id_submission);
  if (periodSubmission) {
    return periodSubmission.period_type;
  }

  // Prefer the filer's chosen submission type over the date window.
  // ECFS treats a COMMENT as a comment regardless of when it was filed;
  // commsgenie's Comment Period tab and comment-summary scope should
  // do the same so we don't drop early/late filings into "Other".
  const typeBucket = periodFromSubmissionType(filing);
  if (typeBucket) return typeBucket;

  const filingDate = filing.date_received || filing.date_submission;
  if (!filingDate) return 'other';

  // Timeline boundary dates (e.g. "2026-05-11T00:00:00+00:00") encode
  // calendar days; read components in UTC so we get the intended day.
  const normalizeDate = (dateStr: string | null) => {
    if (!dateStr) return null;

    let d: Date;

    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [year, month, day] = dateStr.split('-').map(Number);
      d = new Date(Date.UTC(year, month - 1, day));
    } else {
      d = new Date(dateStr);
      if (isNaN(d.getTime())) {
        d = new Date(dateStr + 'Z');
      }
    }

    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  };

  // Real filing timestamps need to be bucketed by ECFS local (Eastern)
  // day. A comment filed at 9pm Eastern on the deadline arrives as
  // 01:00 UTC the next day; reading components in UTC would push it
  // into the reply period even though the filer was within the
  // Eastern-time deadline.
  const easternDateFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  const normalizeFilingDateEastern = (dateStr: string | null) => {
    if (!dateStr) return null;

    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [year, month, day] = dateStr.split('-').map(Number);
      return new Date(Date.UTC(year, month - 1, day));
    }

    let d = new Date(dateStr);
    if (isNaN(d.getTime())) d = new Date(dateStr + 'Z');
    if (isNaN(d.getTime())) return null;

    const parts = easternDateFormatter.formatToParts(d);
    const year = Number(parts.find((p) => p.type === 'year')?.value);
    const month = Number(parts.find((p) => p.type === 'month')?.value);
    const day = Number(parts.find((p) => p.type === 'day')?.value);
    if (!year || !month || !day) return null;

    return new Date(Date.UTC(year, month - 1, day));
  };

  const submissionDateOnly = normalizeFilingDateEastern(filingDate);
  if (!submissionDateOnly) return 'other';

  const timeline = activeTimeline;
  if (!timeline) return 'other';

  const commentsStart = normalizeDate(timeline.comments_period_start);
  const commentsEnd = normalizeDate(timeline.comments_period_end);
  const replyStart = normalizeDate(timeline.reply_comments_period_start);
  const replyEnd = normalizeDate(timeline.reply_comments_period_end);

  const compareDates = (date1: Date, date2: Date): number => {
    const d1Year = date1.getUTCFullYear();
    const d1Month = date1.getUTCMonth();
    const d1Day = date1.getUTCDate();
    const d2Year = date2.getUTCFullYear();
    const d2Month = date2.getUTCMonth();
    const d2Day = date2.getUTCDate();

    if (d1Year !== d2Year) return d1Year - d2Year;
    if (d1Month !== d2Month) return d1Month - d2Month;
    return d1Day - d2Day;
  };

  if (commentsStart && commentsEnd) {
    const dateCompare = compareDates(submissionDateOnly, commentsEnd);
    if (compareDates(submissionDateOnly, commentsStart) >= 0 && dateCompare <= 0) {
      return 'comments';
    }
  } else if (commentsStart) {
    if (compareDates(submissionDateOnly, commentsStart) >= 0) {
      if (commentsEnd) {
        if (compareDates(submissionDateOnly, commentsEnd) <= 0) {
          return 'comments';
        }
      } else if (!replyStart || compareDates(submissionDateOnly, replyStart) < 0) {
        return 'comments';
      }
    }
  }

  if (commentsEnd && replyEnd) {
    const isAfterCommentDeadline = compareDates(submissionDateOnly, commentsEnd) > 0;
    const isBeforeReplyEnd = compareDates(submissionDateOnly, replyEnd) <= 0;

    if (isAfterCommentDeadline && isBeforeReplyEnd) {
      return 'reply_comments';
    }
  } else if (replyStart && replyEnd) {
    if (commentsEnd) {
      const isAfterCommentDeadline = compareDates(submissionDateOnly, commentsEnd) > 0;
      const isInReplyRange =
        compareDates(submissionDateOnly, replyStart) >= 0 &&
        compareDates(submissionDateOnly, replyEnd) <= 0;
      if (isAfterCommentDeadline && isInReplyRange) {
        return 'reply_comments';
      }
    } else {
      if (
        compareDates(submissionDateOnly, replyStart) >= 0 &&
        compareDates(submissionDateOnly, replyEnd) <= 0
      ) {
        return 'reply_comments';
      }
    }
  } else if (commentsEnd && replyStart) {
    const isAfterCommentDeadline = compareDates(submissionDateOnly, commentsEnd) > 0;
    const isBeforeReplyStart = compareDates(submissionDateOnly, replyStart) <= 0;
    if (isAfterCommentDeadline && isBeforeReplyStart) {
      return 'reply_comments';
    }
  }

  return 'other';
}
